import type { IRatingMatrixFrameworkInstrumentTrack } from '__generated__';
import { IInstrumentTrackInputMode } from '__generated__';
import { baseApi } from 'store/api';
import { API, asPatchBody, normalizeError, type PatchBody } from 'utils/api-client';
import { getSortOrderPatches, type SortOrderPatch } from './helpers';

export const ratingMatrixFrameworkInstrumentTrackApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    createInstrumentTrack: builder.mutation<
      IRatingMatrixFrameworkInstrumentTrack,
      {
        frameworkTemplateID: number;
        title: string;
        sortOrder: number;
        inputMode: IInstrumentTrackInputMode;
      }
    >({
      async queryFn(body) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkInstrumentTrackCreateCreate(body);
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    patchInstrumentTrack: builder.mutation<
      string,
      {
        id: number;
        frameworkTemplateID: number;
        patch: PatchBody<Pick<IRatingMatrixFrameworkInstrumentTrack, 'title' | 'sortOrder'>>;
      }
    >({
      async queryFn({ id, patch }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkInstrumentTrackPatchPartialUpdate(
            { id },
            asPatchBody(patch),
          );
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    deleteInstrumentTrack: builder.mutation<
      IRatingMatrixFrameworkInstrumentTrack,
      { id: number; frameworkTemplateID: number }
    >({
      async queryFn({ id }) {
        try {
          const { data } = await API.api.ratingMatrixFrameworkInstrumentTrackDeleteDelete({ id });
          return { data };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),

    reorderInstrumentTracks: builder.mutation<
      void,
      {
        frameworkTemplateID: number;
        items: Array<{ id: number; sortOrder: number }>;
        startIndex: number;
        endIndex: number;
      }
    >({
      async queryFn({ items, startIndex, endIndex }) {
        try {
          const patches = getSortOrderPatches(items, startIndex, endIndex);
          await Promise.all(
            patches.map((patch: SortOrderPatch) =>
              API.api.ratingMatrixFrameworkInstrumentTrackPatchPartialUpdate(
                { id: patch.id },
                asPatchBody({ sortOrder: patch.sortOrder }),
              ),
            ),
          );
          return { data: undefined };
        } catch (error) {
          return { error: normalizeError(error) };
        }
      },
    }),
  }),
});

export const {
  useCreateInstrumentTrackMutation,
  usePatchInstrumentTrackMutation,
  useDeleteInstrumentTrackMutation,
  useReorderInstrumentTracksMutation,
} = ratingMatrixFrameworkInstrumentTrackApi;
